import z from 'zod'

export const APP_NAME = 'Proven Valor'

export const AWS_PROFILE_PHOTOS_FOLDER = 'member-photos'
export const AWS_DISCOUNT_PHOTOS_FOLDER = 'discount-photos'

// Discounts
export const DISCOUNT_CATEGORIES = [
	{ label: 'Food and Beverages', value: 'food' },
	{ label: 'Fashion', value: 'fashion' },
	{ label: 'Electronics', value: 'electronics' },
	{ label: 'eCommerce', value: 'ecommerce' },
] as const

// CRM Response table
export const CRM_RESPONSE_FROM_FULFILLMENT = 'fulfillment'
export const CRM_RESPONSE_FROM_SYNC = 'sync'

// CRM statuses
export const VETERAN_STATUS_VERIFIED = 'verified'
export const VETERAN_STATUS_MISSING_DD214 = 'missing_dd214' // CRM member found, docs found, but not a DD214
export const VETERAN_STATUS_NO_DOCUMENTS = 'no_documents' // CRM member found, but no docs
export const VETERAN_STATUS_NOT_FOUND = 'not_found' // CRM couldn't find that member or the found order is already linked
export const VETERAN_STATUS_PENDING_DOCUMENTS = 'pending_documents' // Ordered documents through PV
export const VETERAN_UNVERIFIED_STATUSES = [
	VETERAN_STATUS_MISSING_DD214,
	VETERAN_STATUS_NO_DOCUMENTS,
	VETERAN_STATUS_NOT_FOUND,
] as const

// Unable to process
export const UNABLE_TO_PROCESS = 'unable_to_process'

// CRM meta-statuses
export const ADMIN_VETERAN_STATUS_PENDING = 'document_sync_not_occurred' // We don't have any CrmResponse records yet
export const ADMIN_VETERAN_SELF_SYNC_FAILED_MULTIPLE_TIMES =
	'document_self_sync_failed_multiple_times' // The user has failed to sync their documents

// Membership sort options
export const MEMBERSHIP_SORT_OPTIONS = [
	{
		label: 'Last Name (A-Z)',
		value: 'last_name_asc',
		order: 'asc',
	},
	{
		label: 'Last Name (Z-A)',
		value: 'last_name_desc',
		order: 'desc',
	},
	{
		label: 'Date Joined (Newest)',
		value: 'date_joined_desc',
		order: 'desc',
	},
	{
		label: 'Date Joined (Oldest)',
		value: 'date_joined_asc',
		order: 'asc',
	},
	{
		label: 'Date Subscribed (Newest)',
		value: 'date_subscribed_desc',
		order: 'desc',
	},
	{
		label: 'Date Subscribed (Oldest)',
		value: 'date_subscribed_asc',
		order: 'asc',
	},
] as const

// Membership Card status
export const MEMBERSHIP_CARD_STATUS_WAITING_FOR_MEMBER_CONFIRMATION =
	'waiting_for_member_confirmation'
export const MEMBERSHIP_CARD_MEMBER_REQUESTED_EDITS = 'member_requested_edits'
export const MEMBERSHIP_CARD_STATUS_READY_TO_SEND_TO_ENGRAVER =
	'ready_to_send_to_engraver'
export const MEMBERSHIP_CARD_STATUS_PROCESSING = 'processing'
export const MEMBERSHIP_CARD_STATUS_SENT_TO_ENGRAVER = 'sent_to_engraver'
export const MEMBERSHIP_CARD_STATUS_SHIPPED = 'shipped'
export const MEMBERSHIP_CARD_STATUS_DELIVERED = 'delivered'
export const MEMBERSHIP_CARD_STATUSES = [
	MEMBERSHIP_CARD_MEMBER_REQUESTED_EDITS,
	MEMBERSHIP_CARD_STATUS_WAITING_FOR_MEMBER_CONFIRMATION,
	MEMBERSHIP_CARD_STATUS_READY_TO_SEND_TO_ENGRAVER,
	MEMBERSHIP_CARD_STATUS_PROCESSING,
	MEMBERSHIP_CARD_STATUS_SENT_TO_ENGRAVER,
	MEMBERSHIP_CARD_STATUS_SHIPPED,
	MEMBERSHIP_CARD_STATUS_DELIVERED,
] as const
export const BATCH_STATUSES = [
	MEMBERSHIP_CARD_STATUS_READY_TO_SEND_TO_ENGRAVER,
	MEMBERSHIP_CARD_STATUS_PROCESSING,
	MEMBERSHIP_CARD_STATUS_SENT_TO_ENGRAVER,
	MEMBERSHIP_CARD_STATUS_SHIPPED,
	MEMBERSHIP_CARD_STATUS_DELIVERED,
] as const
export const MEMBERSHIP_CARD_STATUSES_CAN_EDIT_PROFILE = [
	MEMBERSHIP_CARD_MEMBER_REQUESTED_EDITS,
	MEMBERSHIP_CARD_STATUS_WAITING_FOR_MEMBER_CONFIRMATION,
] as const

export const MEMBERSHIP_CARD_NUMBER_LENGTH = 8

// Billing subscriptions
export const BILLING_SUBSCRIPTION_STATUS_ACTIVE = 'active'
export const BILLING_SUBSCRIPTION_STATUS_CANCELED = 'canceled'
export const BILLING_SUBSCRIPTION_STATUS_NO_SUBSCRIPTION = 'no_subscription'
export const BILLING_SUBSCRIPTION_STATUSES = [
	BILLING_SUBSCRIPTION_STATUS_ACTIVE,
	BILLING_SUBSCRIPTION_STATUS_CANCELED,
	BILLING_SUBSCRIPTION_STATUS_NO_SUBSCRIPTION,
] as const

// Pending Subscription
export const PENDING_SUBSCRIPTION_STATUS_PENDING = 'pending'
export const PENDING_SUBSCRIPTION_STATUS_VERIFICATION_SENT = 'verification_sent'
export const PENDING_SUBSCRIPTION_STATUS_DUPLICATE_EMAIL = 'duplicate_email'
export const PENDING_SUBSCRIPTION_STATUSES = [
	PENDING_SUBSCRIPTION_STATUS_PENDING,
	PENDING_SUBSCRIPTION_STATUS_VERIFICATION_SENT,
	PENDING_SUBSCRIPTION_STATUS_DUPLICATE_EMAIL,
] as const

// CRM
export const CRM_DOCUMENT_TYPE_DD214 = 'DD214'
export const CRM_DOCUMENT_TYPE_OMPF = 'OMPF'
export const CRM_DOCUMENT_TYPE_POINTS_STATEMENT = 'POINTS_STATEMENT'
export const CRM_DOCUMENT_TYPES = [
	CRM_DOCUMENT_TYPE_DD214,
	CRM_DOCUMENT_TYPE_OMPF,
	CRM_DOCUMENT_TYPE_POINTS_STATEMENT,
] as const
export const CRM_UPLOADS_FOLDER = 'uploads'

// Documents
export const DOCUMENT_TYPE_DD214 = 'dd214'
export const DOCUMENT_TYPE_OMPF = 'ompf'
export const DOCUMENT_TYPE_POINTS_STATEMENT = 'points_statement'
export const DOCUMENT_TYPES = [
	{
		label: 'DD214',
		value: DOCUMENT_TYPE_DD214,
		description: 'Discharge Documents - DD214, NGB-22, or Equivalent',
	},
	{
		label: 'OMPF',
		value: DOCUMENT_TYPE_OMPF,
		description: 'OMPF - Official Military Personnel File',
	},
	{
		label: 'Points Statement',
		value: DOCUMENT_TYPE_POINTS_STATEMENT,
		description: 'POINTS',
	},
] as const

export const DOCUMENT_REDACTION_REQUIRED = 'redaction_required'
export const DOCUMENT_NO_PUBLIC_DD214 = 'no_public_dd214'
export const DOCUMENT_STATUS_HAS_DD214 = 'has_dd214'

export const DOCUMENT_FILTER_OPTIONS = [
	{ label: 'No Public DD214', value: DOCUMENT_NO_PUBLIC_DD214 },
	{ label: 'Redaction Required', value: DOCUMENT_REDACTION_REQUIRED },
	{ label: 'Has DD214', value: DOCUMENT_STATUS_HAS_DD214 },
	{ label: 'Unable to Process', value: UNABLE_TO_PROCESS },
] as const

// Stripe / Subscriptions
export const SUBSCRIPTION_PENDING_CONFIRMATION = 'pending_confirmation'
export const SUBSCRIPTION_INCOMPLETE = 'incomplete'
export const SUBSCRIPTION_ACTIVE = 'active'
export const SUBSCRIPTION_CANCELED = 'canceled'

// Roles
export const ROLE_USER = 'user'
export const ROLE_ADMIN = 'admin'
export const ROLE_TEST_USER = 'test_user'
export const ROLE_ORDER_DOCS_EARLY_ACCESS = 'order_documents_early_access'
export const ROLES = [
	ROLE_USER,
	ROLE_ADMIN,
	ROLE_TEST_USER,
	ROLE_ORDER_DOCS_EARLY_ACCESS,
] as const
export const ROLE_ENUM = z.enum(ROLES)
export type Role = z.infer<typeof ROLE_ENUM>

// Military
export const DISCHARGE_CHARACTER_HONORABLE = 'honorable'
export const DISCHARGE_CHARACTER_GENERAL = 'general'
export const DISCHARGE_CHARACTER_OTHER_NO_LISTING = 'under_other_no_listing'

export const DISCHARGE_CHARACTERS = [
	DISCHARGE_CHARACTER_HONORABLE,
	DISCHARGE_CHARACTER_GENERAL,
	DISCHARGE_CHARACTER_OTHER_NO_LISTING,
] as const

export const DISCHARGE_CHARACTER_DROPDOWN_OPTIONS = [
	{ value: DISCHARGE_CHARACTER_HONORABLE, label: 'Honorable' },
	{
		value: DISCHARGE_CHARACTER_GENERAL,
		label: 'Under Honorable Conditions (General)',
	},
	{
		value: DISCHARGE_CHARACTER_OTHER_NO_LISTING,
		label: 'Other - No Listing',
	},
] as const

// Military branches

export const ARMY_BRANCH = 'Army'
export const NAVY_BRANCH = 'Navy'
export const MARINES_BRANCH = 'Marines'
export const COAST_GUARD_BRANCH = 'Coast Guard'
export const AIR_FORCE_BRANCH = 'Air Force'
export const MERCHANT_MARINES_BRANCH = 'Merchant Marines'
export const SPACE_FORCE_BRANCH = 'Space Force'
export const ACTIVE_BRANCHES = [
	{ value: ARMY_BRANCH, label: 'US Army' },
	{ value: NAVY_BRANCH, label: 'US Navy' },
	{ value: MARINES_BRANCH, label: 'US Marine Corps' },
	{ value: COAST_GUARD_BRANCH, label: 'US Coast Guard' },
	{ value: AIR_FORCE_BRANCH, label: 'US Air Force' },
	{ value: MERCHANT_MARINES_BRANCH, label: 'Merchant Marines' },
	{ value: SPACE_FORCE_BRANCH, label: 'US Space Force' },
]

export const ARMY_RESERVE_BRANCH = 'Army Reserve'
export const NAVY_RESERVE_BRANCH = 'Navy Reserve'
export const MARINES_RESERVE_BRANCH = 'US Marine Corps Reserve'
export const AIR_FORCE_RESERVE_BRANCH = 'Air Force Reserve'
export const COAST_GUARD_RESERVE_BRANCH = 'Coast Guard Reserve'
export const RESERVED_BRANCHES = [
	{ value: ARMY_RESERVE_BRANCH, label: 'Army Reserve' },
	{ value: NAVY_RESERVE_BRANCH, label: 'Navy Reserve' },
	{ value: MARINES_RESERVE_BRANCH, label: 'US Marine Corps Reserve' },
	{ value: AIR_FORCE_RESERVE_BRANCH, label: 'Air Force Reserve' },
	{ value: COAST_GUARD_RESERVE_BRANCH, label: 'Coast Guard Reserve' },
]

export const ARMY_NATIONAL_GUARD_BRANCH = 'Army National Guard'
export const AIR_NATIONAL_GUARD_BRANCH = 'Air National Guard'
export const NATIONAL_GUARD_BRANCHES = [
	{ value: ARMY_NATIONAL_GUARD_BRANCH, label: ARMY_NATIONAL_GUARD_BRANCH },
	{ value: AIR_NATIONAL_GUARD_BRANCH, label: AIR_NATIONAL_GUARD_BRANCH },
]

export const MILITARY_ACADEMY_BRANCH = 'Military Academy, West Point'
export const NAVAL_ACADEMY_BRANCH = 'Naval Academy, Annapolis'
export const AIR_FORCE_ACADEMY_BRANCH = 'Air Force Academy, Colorado Springs'
export const ACADEMY_SERVICE_BRANCHES = [
	{ value: MILITARY_ACADEMY_BRANCH, label: MILITARY_ACADEMY_BRANCH },
	{ value: NAVAL_ACADEMY_BRANCH, label: NAVAL_ACADEMY_BRANCH },
	{ value: AIR_FORCE_ACADEMY_BRANCH, label: AIR_FORCE_ACADEMY_BRANCH },
]

export const PUBLIC_HEALTH_SERVICE_BRANCH = 'Public Health Service'
export const OTHER_BRANCHES = [
	{ value: PUBLIC_HEALTH_SERVICE_BRANCH, label: PUBLIC_HEALTH_SERVICE_BRANCH },
]

export const BRANCH_OF_SERVICE_OPTIONS = [
	{ label: 'Active', options: ACTIVE_BRANCHES },
	{ label: 'Reserve', options: RESERVED_BRANCHES },
	{ label: 'State National Guard', options: NATIONAL_GUARD_BRANCHES },
	{ label: 'Service Academies', options: ACADEMY_SERVICE_BRANCHES },
	{ label: 'Others', options: OTHER_BRANCHES },
]

export const OFFICER_OR_ENLISTED_OPTIONS = [
	{ value: 'enlisted', label: 'Enlisted' },
	{ value: 'officer', label: 'Officer' },
	{ value: 'unsure', label: 'Unsure' },
]

// CRM compatible military branches
export const CRM_BRANCHES_COMPATIBILITY: Record<string, string> = {
	[ARMY_BRANCH]: 'US Army',
	[NAVY_BRANCH]: 'US Navy',
	[MARINES_BRANCH]: 'US Marine Corps',
	[COAST_GUARD_BRANCH]: 'US Coast Guard',
	[AIR_FORCE_BRANCH]: 'US Air Force',
	[MERCHANT_MARINES_BRANCH]: 'Merchant Marines',
	[SPACE_FORCE_BRANCH]: 'US Space Force',

	[ARMY_RESERVE_BRANCH]: 'Army Reserve',
	[NAVY_RESERVE_BRANCH]: 'Navy Reserve',
	[MARINES_RESERVE_BRANCH]: 'Marine Corps Reserve',
	[AIR_FORCE_RESERVE_BRANCH]: 'Air Force Reserve',
	[COAST_GUARD_RESERVE_BRANCH]: 'Coast Guard Reserve',

	[ARMY_NATIONAL_GUARD_BRANCH]: 'Army National Guard',
	[AIR_NATIONAL_GUARD_BRANCH]: 'Air National Guard',

	[MILITARY_ACADEMY_BRANCH]: 'Military Academy, West Point',
	[NAVAL_ACADEMY_BRANCH]: 'Naval Academy, Annapolis',
	[AIR_FORCE_ACADEMY_BRANCH]: 'Air Force Academy, Colorado Springs',
}

// US States
export const US_STATES_OPTIONS = [
	{ value: 'AL', label: 'Alabama' },
	{ value: 'AK', label: 'Alaska' },
	{ value: 'AZ', label: 'Arizona' },
	{ value: 'AR', label: 'Arkansas' },
	{ value: 'CA', label: 'California' },
	{ value: 'CO', label: 'Colorado' },
	{ value: 'CT', label: 'Connecticut' },
	{ value: 'DC', label: 'District of Columbia' },
	{ value: 'DE', label: 'Delaware' },
	{ value: 'FL', label: 'Florida' },
	{ value: 'GA', label: 'Georgia' },
	{ value: 'GU', label: 'Guam' },
	{ value: 'HI', label: 'Hawaii' },
	{ value: 'IA', label: 'Iowa' },
	{ value: 'ID', label: 'Idaho' },
	{ value: 'IL', label: 'Illinois' },
	{ value: 'IN', label: 'Indiana' },
	{ value: 'KS', label: 'Kansas' },
	{ value: 'KY', label: 'Kentucky' },
	{ value: 'LA', label: 'Louisiana' },
	{ value: 'MA', label: 'Massachusetts' },
	{ value: 'ME', label: 'Maine' },
	{ value: 'MD', label: 'Maryland' },
	{ value: 'MI', label: 'Michigan' },
	{ value: 'MN', label: 'Minnesota' },
	{ value: 'MO', label: 'Missouri' },
	{ value: 'MS', label: 'Mississippi' },
	{ value: 'MT', label: 'Montana' },
	{ value: 'NC', label: 'North Carolina' },
	{ value: 'ND', label: 'North Dakota' },
	{ value: 'NE', label: 'Nebraska' },
	{ value: 'NH', label: 'New Hampshire' },
	{ value: 'NJ', label: 'New Jersey' },
	{ value: 'NM', label: 'New Mexico' },
	{ value: 'NV', label: 'Nevada' },
	{ value: 'NY', label: 'New York' },
	{ value: 'OH', label: 'Ohio' },
	{ value: 'OK', label: 'Oklahoma' },
	{ value: 'OR', label: 'Oregon' },
	{ value: 'PA', label: 'Pennsylvania' },
	{ value: 'PR', label: 'Puerto Rico' },
	{ value: 'RI', label: 'Rhode Island' },
	{ value: 'SC', label: 'South Carolina' },
	{ value: 'SD', label: 'South Dakota' },
	{ value: 'TN', label: 'Tennessee' },
	{ value: 'TX', label: 'Texas' },
	{ value: 'UT', label: 'Utah' },
	{ value: 'VA', label: 'Virginia' },
	{ value: 'VI', label: 'United States Virgin Islands' },
	{ value: 'VT', label: 'Vermont' },
	{ value: 'WA', label: 'Washington' },
	{ value: 'WI', label: 'Wisconsin' },
	{ value: 'WV', label: 'West Virginia' },
	{ value: 'WY', label: 'Wyoming' },
]

export const MAX_FILE_SIZE = 1024 * 1024 * 3 // 3MB

// ! Devs: this was a complete guess. Please adjust this number if we have memory / "cannot convert PDF to image - too large" errors in Production
export const MAX_PDF_PROCESSING_SIZE = 1024 * 1024 * 10 // 10MB

export const PDF_PROCESSING_ERROR_CORRUPTED =
	'The PDF file appears to be in an unsupported format or is corrupted.'
export const PDF_PROCESSING_IMAGE_COMPOSITE_DIMENSION_ERROR =
	'PDF cannot be processed by Redaction Tool because one or more pages are of varied width or height'
